import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import {
  ComparisonObject,
  ComparisonOperator,
  LogicalComparisonOperator,
} from 'src/app/core/api/models';
import { ComparisonOperatorEnumDisplay } from 'src/app/core/enums/display/comparison-operator.display';
import { AppFormFieldSearcherType } from 'src/app/core/types/app-form-field-searcher-type.type';
import { FieldSearcherOutput } from 'src/app/shared/global/field-searcher/field-searcher-event.model';
import { BaseComponentComponent } from '../../base/base-component/base-component.component';

@Component({
    selector: 'form-field-searcher',
    templateUrl: './form-field-searcher.component.html',
    styleUrls: [`form-field-searcher.component.scss`],
    standalone: false
})
export class FormFieldSearcherComponent
  extends BaseComponentComponent
  implements OnInit
{
  //#region properties

  @Input() control: FormControl | any;
  @Input() fieldName: string;
  @Input() label: string;
  @Input() enumName: string;
  @Input() type: AppFormFieldSearcherType = 'text';
  @Input() operator: ComparisonOperator;
  @Input() isOnlyNumber: boolean;
  @Input() hasCurrency: boolean;
  @Input() hasSeparator: boolean = true;
  @Input() value: any;

  comparisonItemList: ComparisonObject[] = [];
  @Input() selectedEnumValues: any[] = [];

  @Input() resetField: any;

  get ComparisonOperator() {
    return ComparisonOperator;
  }
  public ComparisonOperatorEnumDisplay: typeof ComparisonOperatorEnumDisplay =
    ComparisonOperatorEnumDisplay;

  numberOperators = [
    ComparisonOperator.EqualTo,
    ComparisonOperator.NotEqualTo,
    ComparisonOperator.GreaterThan,
    ComparisonOperator.LessThan,
    ComparisonOperator.GreaterThanOrEqual,
    ComparisonOperator.LessThanOrEqual,
  ];

  textOperators = [
    ComparisonOperator.Contains,
    ComparisonOperator.StartsWith,
    ComparisonOperator.EndsWith,
  ];

  operatorIcons = [
    //Number
    'mdi-menu-left', // none
    'mdi-code-equal', //EqualTo
    'mdi-code-not-equal', //NotEqualTo
    'mdi-code-greater-than', //GreaterThan
    'mdi-code-less-than', //LessThan
    'mdi-code-greater-than-or-equal', //GreaterThanOrEqual
    'mdi-code-less-than-or-equal', //LessThanOrEqual
    //String
    'mdi-text-box-search-outline', //Contains
    'mdi-text-box-remove-outline', //NotContains
    'mdi-arrow-left', //StartsWith
    'mdi-arrow-right', //EndsWith
  ];

  @Input() comparisonObject: ComparisonObject = {};
  @Output() comparisonObjectChanges = new EventEmitter<FieldSearcherOutput>();

  //#endregion

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.initModel();
  }

  initModel() {
    this.comparisonObject.field = this.fieldName;

    switch (this.type) {
      case 'text':
        this.operator = ComparisonOperator.Contains;
        break;
      case 'boolean':
        this.value = 'none';
        this.operator = ComparisonOperator.Contains;
        break;
      default:
        this.operator = ComparisonOperator.EqualTo;
        break;
    }

    this.comparisonObject.operator = this.operator;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['needReset']) this.onReset();
  }

  onChangeValue() {
    if (this.type == 'boolean' && this.value == 'none')
      this.comparisonObject.value = '';
    else if (this.value == null) this.comparisonObject.value = '';
    else
      this.comparisonObject.value = this.value
        .toString()
        .trim()
        .replace(/,/g, '');

    this.emit();
  }

  onChangeOperator(event) {
    this.operator = event;
    this.comparisonObject.operator = this.operator;
    this.emit();
  }

  onClearValue() {
    this.value = '';
    this.comparisonObject.value = '';
    this.emit();
  }

  onReset() {
    this.value = '';
    this.comparisonObject = {};
    this.emit();

    this.initModel();
  }

  removeEmptyValueFromComparisonItemList() {
    this.comparisonItemList = this.comparisonItemList.filter(
      (item) => item.value || item.value == '0'
    );
  }

  addOrRemoveEnumValue(event: any[]) {
    this.selectedEnumValues = event;

    this.comparisonItemList = [];

    this.selectedEnumValues.forEach((value) => {
      this.comparisonItemList.push({
        field: this.fieldName,
        operator: +this.comparisonObject.operator,
        value,
      });
    });

    this.emit();
  }

  emit() {
    if (this.type != 'enum') {
      this.comparisonItemList = [];
      this.comparisonItemList.push(this.comparisonObject);
    }

    let logicalComparisonOperator;
    switch (this.type) {
      case 'dateTime':
      case 'number':
        logicalComparisonOperator = LogicalComparisonOperator.And;
        break;
      case 'enum':
        logicalComparisonOperator = LogicalComparisonOperator.Or;
        break;
      default:
        logicalComparisonOperator = null;
        break;
    }

    this.removeEmptyValueFromComparisonItemList();

    const comparisonObjectsWithLogic: FieldSearcherOutput = {
      comparisonObjects: this.comparisonItemList,
      fieldName: this.fieldName,
    };

    if (logicalComparisonOperator != null)
      comparisonObjectsWithLogic.logicalComparisonOperator =
        logicalComparisonOperator;

    this.comparisonObjectChanges.emit(
      Object.assign({}, comparisonObjectsWithLogic)
    );
  }
}
